import { Crown, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { openEliteWhatsApp } from '../lib/constants';

/**
 * V14.5 Part 19w — Elite Program upgrade card.
 *
 * Founder ask (2026-05-21): replace the unfriendly "This content isn't
 * available for purchase right now." copy with a clear value-prop and a
 * direct path to upgrade. Renders wherever a course/episode is locked AND
 * has no purchasable tier — the learner can DM us on WhatsApp instead of
 * hitting a dead-end.
 *
 * Two visual variants:
 *   • size="full"    — large banner used at the top of a locked course page
 *   • size="compact" — single-line strip used inside locked episode rows
 */
export default function EliteUpgradeCard({ size = 'full', testId }) {
  const navigate = useNavigate();
  const goToElite = () => navigate('/learner/elite');
  if (size === 'compact') {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={goToElite}
        className="text-xs h-7 border-amber-200 bg-amber-50/60 text-amber-800 hover:bg-amber-100 hover:text-amber-900 font-medium"
        data-testid={testId || 'elite-unlock-compact-btn'}
      >
        <Crown className="h-3 w-3 mr-1 text-amber-600" />
        Unlock
      </Button>
    );
  }

  return (
    <Card
      className="overflow-hidden rounded-2xl border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50"
      data-testid={testId || 'elite-unlock-card'}
    >
      <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Crown className="h-6 w-6 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-heading font-semibold text-slate-900">
            This course is part of the Elite Program
          </p>
          <p className="text-xs sm:text-sm text-slate-700 mt-0.5">
            Elite members unlock every course, live classes, 1:1 dietician
            support and your full prenatal journey with a personal care team.
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            type="button"
            onClick={goToElite}
            className="bg-amber-600 hover:bg-amber-700 text-white font-medium"
            data-testid="elite-unlock-cta-btn"
          >
            <Crown className="h-4 w-4 mr-2" />
            View Elite Program
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openEliteWhatsApp}
            className="text-xs text-amber-800 hover:bg-amber-100"
            data-testid="elite-unlock-whatsapp-btn"
          >
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
            Chat with us
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
